/**
 * Missing Data Explanation System
 * 
 * A lightweight, reusable explanation layer for empty/unavailable analytical fields.
 * Provides standardized explanations for why data is missing and how to rectify it.
 * 
 * DOES NOT add new calculations - only explains existing empty states.
 */

// ============================================================================
// STATUS TYPES
// ============================================================================

export const STATUS_TYPES = {
  UNAVAILABLE: 'Unavailable',
  NOT_YET_AVAILABLE: 'Not yet available',
  NOT_APPLICABLE: 'Not applicable',
  INSUFFICIENT_EVIDENCE: 'Insufficient stable evidence',
  PARTIALLY_AVAILABLE: 'Partially available',
};

// ============================================================================
// FIELD EXPLANATIONS MAP
// ============================================================================

/**
 * Centralized mapping of field IDs to their explanation templates.
 * Each entry contains status-specific explanations with reason, requirements, and rectify actions.
 */
export const FIELD_EXPLANATIONS = {
  // -------------------------------------------------------------------------
  // BIOMASS-DENSITY ESTIMATE
  // -------------------------------------------------------------------------
  biomassDensity: {
    insufficientSamples: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Not enough valid sample points for reliable estimation.',
      requirements: ['At least 2-3 valid sample comparisons'],
      rectify: ['Upload or record more sample data'],
    },
    highVolatility: {
      status: STATUS_TYPES.INSUFFICIENT_EVIDENCE,
      reason: 'Deviation volatility is too high for a reliable estimate.',
      requirements: [
        'At least 3 valid sample points',
        'Stable positive deviation from model',
        'Low deviation volatility',
        'No major sample quality issues',
      ],
      rectify: [
        'Add more sample points',
        'Improve sampling consistency',
        'Wait for further stable sample updates',
      ],
    },
    noPositiveDeviation: {
      status: STATUS_TYPES.NOT_APPLICABLE,
      reason: 'No sustained positive deviation detected to suggest biomass reduction.',
      requirements: ['Positive deviation above threshold for multiple samples'],
      rectify: ['Continue monitoring - this may indicate healthy stock levels'],
    },
    weakSignal: {
      status: STATUS_TYPES.INSUFFICIENT_EVIDENCE,
      reason: 'Signal strength is too weak to produce reliable biomass estimate.',
      requirements: ['Stronger, more consistent deviation signal'],
      rectify: ['Add more sample points', 'Wait for pattern stabilization'],
    },
  },

  // -------------------------------------------------------------------------
  // EARLIEST ACTIONABLE DETECTION
  // -------------------------------------------------------------------------
  earliestActionable: {
    insufficientSequence: {
      status: STATUS_TYPES.NOT_YET_AVAILABLE,
      reason: 'Not enough stable sequential data to identify when deviation began.',
      requirements: [
        'At least 2-3 consecutive comparable samples',
        'Persistent deviation outside normal range',
      ],
      rectify: ['Add more sample history', 'Wait for future sample updates'],
    },
    noSignificantDeviation: {
      status: STATUS_TYPES.NOT_APPLICABLE,
      reason: 'No significant deviation detected that requires actionable date.',
      requirements: ['Deviation exceeding threshold (typically >5%)'],
      rectify: ['No action needed - stock appears within normal range'],
    },
    volatilePattern: {
      status: STATUS_TYPES.INSUFFICIENT_EVIDENCE,
      reason: 'Deviation pattern is too volatile to pinpoint a reliable start date.',
      requirements: ['More consistent deviation pattern'],
      rectify: ['Improve sampling consistency', 'Add more sample points'],
    },
  },

  // -------------------------------------------------------------------------
  // FEEDING IMPACT CLASSIFICATION
  // -------------------------------------------------------------------------
  feedingImpact: {
    noFeedData: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Feed allocation data has not been provided.',
      requirements: ['Feed records for the relevant period'],
      rectify: ['Upload or enter feed usage data'],
    },
    noCostBasis: {
      status: STATUS_TYPES.PARTIALLY_AVAILABLE,
      reason: 'Feed quantity can be assessed, but cost cannot be calculated.',
      requirements: ['Feed cost per kg'],
      rectify: ['Enter feed cost basis'],
    },
    noDeviation: {
      status: STATUS_TYPES.NOT_APPLICABLE,
      reason: 'No significant deviation detected that would affect feeding interpretation.',
      requirements: ['Positive deviation indicating potential stock loss'],
      rectify: ['No action needed - feed efficiency appears normal'],
    },
    insufficientDeviation: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Deviation is too low to classify meaningful feeding impact.',
      requirements: ['Average positive deviation above 5%'],
      rectify: ['Continue monitoring for stronger deviation signals'],
    },
  },

  // -------------------------------------------------------------------------
  // SAMPLE RELIABILITY
  // -------------------------------------------------------------------------
  sampleReliability: {
    insufficientSamples: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Sample history is insufficient for reliability assessment.',
      requirements: ['Enough sample points', 'Valid dates', 'Valid sample weights'],
      rectify: ['Upload additional sample history', 'Correct missing sample dates or values'],
    },
    invalidData: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Sample data contains invalid or missing values.',
      requirements: ['Valid sample dates', 'Valid weight measurements'],
      rectify: ['Review and correct sample entries'],
    },
  },

  // -------------------------------------------------------------------------
  // STOCK INTEGRITY SCORE
  // -------------------------------------------------------------------------
  stockIntegrity: {
    noModuleOutputs: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Not enough supporting module outputs are available.',
      requirements: [
        'Snapshot signal (FCR analysis)',
        'Growth signal (Growth vs Model analysis)',
        'Spreadsheet persistence or equivalent historical input',
      ],
      rectify: ['Complete additional analysis sections', 'Upload historical data where needed'],
    },
    partialData: {
      status: STATUS_TYPES.PARTIALLY_AVAILABLE,
      reason: 'Only partial module outputs are available for scoring.',
      requirements: ['Additional analysis module outputs'],
      rectify: ['Run remaining analysis modules for complete assessment'],
    },
  },

  // -------------------------------------------------------------------------
  // GROWTH SIGNAL ANALYSIS
  // -------------------------------------------------------------------------
  growthSignal: {
    insufficientSamples: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Not enough sample points for growth signal analysis.',
      requirements: ['At least 2 sample points after arrival'],
      rectify: ['Add more sample measurements'],
    },
    noPattern: {
      status: STATUS_TYPES.NOT_APPLICABLE,
      reason: 'No discernible growth pattern detected.',
      requirements: ['Consistent deviation pattern across samples'],
      rectify: ['Add more sample history for pattern detection'],
    },
  },

  // -------------------------------------------------------------------------
  // DEVIATION PATTERN
  // -------------------------------------------------------------------------
  deviationPattern: {
    insufficientData: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Not enough data points to classify deviation pattern.',
      requirements: ['At least 2-3 sample comparisons'],
      rectify: ['Add more sample measurements'],
    },
  },

  // -------------------------------------------------------------------------
  // DEVIATION STREAK
  // -------------------------------------------------------------------------
  deviationStreak: {
    noStreak: {
      status: STATUS_TYPES.NOT_APPLICABLE,
      reason: 'No consecutive positive deviation streak detected.',
      requirements: ['Consecutive samples with positive deviation'],
      rectify: ['Monitor future samples - no action needed if within range'],
    },
  },

  // -------------------------------------------------------------------------
  // DEVIATION STABILITY
  // -------------------------------------------------------------------------
  deviationStability: {
    insufficientData: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Not enough data to assess deviation stability.',
      requirements: ['At least 3 sample points'],
      rectify: ['Add more sample history'],
    },
  },

  // -------------------------------------------------------------------------
  // DIVERGENCE DETECTION
  // -------------------------------------------------------------------------
  divergenceDetection: {
    notDetected: {
      status: STATUS_TYPES.NOT_APPLICABLE,
      reason: 'No sustained divergence from model detected.',
      requirements: ['Significant deviation maintained across samples'],
      rectify: ['No action needed - growth aligns with model'],
    },
    insufficientData: {
      status: STATUS_TYPES.NOT_YET_AVAILABLE,
      reason: 'Not enough sequential data to detect divergence point.',
      requirements: ['At least 2 consecutive samples'],
      rectify: ['Add more sample history'],
    },
  },

  // -------------------------------------------------------------------------
  // BIOLOGICAL INTERPRETATION
  // -------------------------------------------------------------------------
  biologicalInterpretation: {
    insufficientSignals: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Not enough analytical signals for biological interpretation.',
      requirements: ['Pattern, stability, and deviation data'],
      rectify: ['Complete growth analysis with more sample points'],
    },
  },

  // -------------------------------------------------------------------------
  // HISTORICAL DEVIATION ANALYSIS (Spreadsheet)
  // -------------------------------------------------------------------------
  historicalDeviation: {
    insufficientHistory: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Not enough historical data for deviation analysis.',
      requirements: ['Multiple sample points over time'],
      rectify: ['Upload spreadsheet with more historical samples'],
    },
    noSignificantPattern: {
      status: STATUS_TYPES.NOT_APPLICABLE,
      reason: 'No significant historical deviation pattern detected.',
      requirements: ['Sustained deviation from model over time'],
      rectify: ['Continue monitoring - current alignment may be normal'],
    },
  },

  // -------------------------------------------------------------------------
  // STRONGEST DEVIATION WINDOW
  // -------------------------------------------------------------------------
  strongestWindow: {
    insufficientData: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Not enough consecutive samples to identify strongest deviation window.',
      requirements: ['At least 3 consecutive sample points'],
      rectify: ['Add more sample history'],
    },
    noSignificantDeviation: {
      status: STATUS_TYPES.NOT_APPLICABLE,
      reason: 'No significant deviation window detected.',
      requirements: ['Sustained deviation above threshold'],
      rectify: ['No action needed - deviations are within normal range'],
    },
  },

  // -------------------------------------------------------------------------
  // ANALYTICAL STRENGTH SCORE
  // -------------------------------------------------------------------------
  analyticalStrength: {
    insufficientData: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Not enough data to calculate analytical strength score.',
      requirements: ['Valid sample points with dates', 'Reasonable sampling intervals'],
      rectify: ['Add more sample data', 'Ensure samples have valid dates'],
    },
  },

  // -------------------------------------------------------------------------
  // DATA QUALITY WARNINGS
  // -------------------------------------------------------------------------
  dataQualityWarnings: {
    noWarnings: {
      status: STATUS_TYPES.NOT_APPLICABLE,
      reason: 'Data quality is acceptable - no warnings to display.',
      requirements: [],
      rectify: [],
    },
  },

  // -------------------------------------------------------------------------
  // FCR DEVIATION CLASSIFICATION (Snapshot)
  // -------------------------------------------------------------------------
  fcrDeviation: {
    noAnalysis: {
      status: STATUS_TYPES.NOT_YET_AVAILABLE,
      reason: 'FCR analysis has not been run.',
      requirements: ['Observed FCR value', 'Model FCR value'],
      rectify: ['Run Snapshot Analysis first'],
    },
    invalidInputs: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Invalid or missing FCR values.',
      requirements: ['Valid observed biological FCR', 'Valid model FCR'],
      rectify: ['Enter valid FCR values'],
    },
  },

  // -------------------------------------------------------------------------
  // PEER / COHORT COMPARISON
  // -------------------------------------------------------------------------
  peerComparison: {
    noPeerData: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'No comparable peer cage data is available.',
      requirements: ['Cages from same cycle, batch, or site'],
      rectify: ['Upload or connect peer cage data'],
    },
    insufficientPeers: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Not enough peer cages for meaningful comparison.',
      requirements: ['At least 2 comparable peer cages'],
      rectify: ['Add more peer cage data'],
    },
  },

  // -------------------------------------------------------------------------
  // FISH COUNT ESTIMATE
  // -------------------------------------------------------------------------
  fishCountEstimate: {
    noStartingCount: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Starting fish count not provided.',
      requirements: ['Initial fish count at arrival'],
      rectify: ['Enter starting fish number'],
    },
    noDeviationData: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Cannot estimate fish count without deviation data.',
      requirements: ['Valid growth deviation analysis'],
      rectify: ['Run Growth vs Model analysis first'],
    },
  },

  // -------------------------------------------------------------------------
  // CONFIDENCE SCORE
  // -------------------------------------------------------------------------
  confidenceScore: {
    insufficientData: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Not enough data to calculate confidence score.',
      requirements: ['Multiple sample points', 'Valid deviation data'],
      rectify: ['Add more sample history'],
    },
  },

  // -------------------------------------------------------------------------
  // TREND CLASSIFICATION
  // -------------------------------------------------------------------------
  trendClassification: {
    insufficientData: {
      status: STATUS_TYPES.UNAVAILABLE,
      reason: 'Not enough data to classify trend.',
      requirements: ['At least 2 sample comparisons'],
      rectify: ['Add more sample measurements'],
    },
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get explanation for a specific field and condition
 * @param {string} fieldId - The field identifier (e.g., 'biomassDensity')
 * @param {string} conditionKey - The specific condition (e.g., 'insufficientSamples')
 * @returns {Object|null} Explanation object or null if not found
 */
export const getFieldExplanation = (fieldId, conditionKey) => {
  const field = FIELD_EXPLANATIONS[fieldId];
  if (!field) return null;
  return field[conditionKey] || null;
};

/**
 * Format explanation for display (inline compact format)
 * @param {Object} explanation - Explanation object from FIELD_EXPLANATIONS
 * @returns {Object} Formatted explanation with display strings
 */
export const formatExplanation = (explanation) => {
  if (!explanation) return null;
  
  return {
    status: explanation.status,
    reason: explanation.reason,
    requirements: explanation.requirements?.join('; ') || '',
    rectify: explanation.rectify?.join('; ') || '',
    // Compact single-line format for exports/PDFs
    compact: `${explanation.status} (${explanation.reason.toLowerCase()})`,
    // Full display format
    full: {
      status: explanation.status,
      reason: explanation.reason,
      requirements: explanation.requirements || [],
      rectify: explanation.rectify || [],
    },
  };
};

/**
 * Get compact explanation string for PDF/export use
 * @param {string} fieldId - The field identifier
 * @param {string} conditionKey - The specific condition
 * @returns {string} Compact explanation string
 */
export const getCompactExplanation = (fieldId, conditionKey) => {
  const explanation = getFieldExplanation(fieldId, conditionKey);
  if (!explanation) return 'Unavailable';
  return `${explanation.status} (${explanation.reason.toLowerCase()})`;
};

/**
 * Determine the explanation condition based on data state
 * This helper maps common data states to condition keys
 * @param {string} fieldId - The field identifier
 * @param {Object} dataState - Object containing relevant data state info
 * @returns {string|null} Condition key or null
 */
export const determineCondition = (fieldId, dataState) => {
  const { 
    sampleCount = 0, 
    hasData = false, 
    isVolatile = false,
    hasPositiveDeviation = false,
    hasFeedData = false,
    reliabilityScore = null,
    signalStrength = null,
  } = dataState;

  switch (fieldId) {
    case 'biomassDensity':
      if (sampleCount < 2) return 'insufficientSamples';
      if (isVolatile) return 'highVolatility';
      if (!hasPositiveDeviation) return 'noPositiveDeviation';
      if (signalStrength === 'Weak') return 'weakSignal';
      return null;

    case 'earliestActionable':
      if (sampleCount < 2) return 'insufficientSequence';
      if (!hasPositiveDeviation) return 'noSignificantDeviation';
      if (isVolatile) return 'volatilePattern';
      return null;

    case 'feedWaste':
      if (!hasFeedData) return 'noFeedData';
      if (!hasPositiveDeviation) return 'noDeviation';
      return null;

    case 'sampleReliability':
      if (sampleCount < 1) return 'insufficientSamples';
      if (!hasData) return 'invalidData';
      return null;

    case 'stockIntegrity':
      if (!hasData) return 'noModuleOutputs';
      return 'partialData';

    default:
      if (sampleCount < 2) return 'insufficientData';
      return null;
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  STATUS_TYPES,
  FIELD_EXPLANATIONS,
  getFieldExplanation,
  formatExplanation,
  getCompactExplanation,
  determineCondition,
};
